import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { getEngineerDetails } from '../../../../redux/Slices/EngineersSlice';
import { toast } from 'react-toastify';

const ViewEngineer = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        const payload = await dispatch(getEngineerDetails(id)).unwrap();
        setDetails(payload?.data || payload);
      } catch (error) {
        toast.error(error || t('engineers.view.messages.fetchError'), { rtl: isRTL });
        navigate('/engineers');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, id, navigate]);

  if (isLoading) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, textAlign: isRTL ? 'right' : 'left' }}>{t('engineers.view.loading')}</div>
      </div>
    );
  }

  if (!details) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, textAlign: isRTL ? 'right' : 'left' }}>{t('engineers.view.noData')}</div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>{t('engineers.view.title')}</h2>
          <button style={secondaryButtonStyle} onClick={() => navigate('/engineers')}>
            {t('engineers.view.back')}
          </button>
        </div>
        <div style={detailsGrid}>
          {isRTL ? (
            <>
              <InfoRow label={t('engineers.view.nameAr')} value={details.name_ar || details.name_en || '—'} isRTL={true} />
              <InfoRow label={t('engineers.view.nameEn')} value={details.name_en || '—'} isRTL={false} />
            </>
          ) : (
            <>
              <InfoRow label={t('engineers.view.nameEn')} value={details.name_en || details.name_ar || '—'} isRTL={false} />
              <InfoRow label={t('engineers.view.nameAr')} value={details.name_ar || '—'} isRTL={true} />
            </>
          )}
          <InfoRow label={t('engineers.view.taxNumber')} value={details.tax_number} isRTL={isRTL} />
          <InfoRow label={t('engineers.view.phone')} value={details.phone} isRTL={isRTL} />
          <InfoRow label={t('engineers.view.email')} value={details.email} isRTL={false} />
          <InfoRow label={t('engineers.view.address')} value={details.address} isRTL={isRTL} />
          <InfoRow label={t('engineers.view.status')} value={details.is_active ? t('engineers.view.active') : t('engineers.view.inactive')} isRTL={isRTL} />
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, isRTL = true }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
    <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: 'bold' }}>{value || '—'}</span>
  </div>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 700,
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
};

const detailsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '10px 18px',
  borderRadius: 8,
  cursor: 'pointer',
};

export default ViewEngineer;

